import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  createIncidentWithEvidenceApi,
  updateIncidentWithEvidenceApi,
  deleteIncidentWithEvidenceApi,
  uploadIncidentEvidenceApi,
  fetchYardsApi,
  ListarIncidenciasPorPatioYFecha,
} from '../../services/api';
import {
  estadoIncidencias,
  iconAltoPng,
  iconBajoPng,
  iconMedioPng,
  nivelIncidencia,
  peDate,
  peTime,
  tipoIncidencia,
  tipoIncidenciaArray,
} from '../../util/util';
import EvidenciasPorIncidencias from '../../components/Reports/ModalRondas/EvidenciasPorIncidencias';
import { Cargando } from '../../components/Cargando';
import MapLeaflet from '../../components/MapLeaflet';

const LEVEL_OPTIONS = [
  { value: '1', label: nivelIncidencia(1) },
  { value: '2', label: nivelIncidencia(2) },
  { value: '3', label: nivelIncidencia(3) },
];

const TYPE_OPTIONS = tipoIncidenciaArray.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const STATE_OPTIONS = estadoIncidencias.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const formatDateTimeForInput = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const localDateTimeToIso = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

export default function Incidencias() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editMeta, setEditMeta] = useState({ guardName: '' });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [IncidentMap, setIncidentMap] = useState({});

  const [yards, setYards] = useState([]);
  const [yardLoading, setYardLoading] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [yardId, setYardId] = useState('');
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastQuery, setLastQuery] = useState(null);
  const [reloading, setReloading] = useState(false);

  const [form, setForm] = useState({
    description: '',
    creationDate: '',
    typeIncident: '',
    levelIncident: '',
    state: '',
    isEmergency: false,
    incidentResult: '',
  });
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const stateLabels = useMemo(() => {
    const map = new Map();
    STATE_OPTIONS.forEach((option) => {
      map.set(Number(option.value), option.label);
    });
    return map;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) return;
      try {
        setYardLoading(true);
        const dataYards = await fetchYardsApi({ token });
        // console.log(dataYards);
        if (!cancelled) {
          setYards(Array.isArray(dataYards) ? dataYards : []);
        }
      } catch (err) {
        if (!cancelled) {
          setYards([]);
          console.error('Failed to load yards', err);
        }
      } finally {
        if (!cancelled) {
          setYardLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const pushToast = useCallback((type, msg) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, msg }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 4000);
  }, []);

  const runQuery = useCallback(async (filters, { showSpinner = true, toastOnError = true } = {}) => {
    if (!token) return;
    const sanitized = {
      yardId: filters?.yardId ?? '',
      startDate: filters?.startDate ?? '',
      endDate: filters?.endDate ?? '',
    };

    if (showSpinner) {
      setSearching(true);
    }

    try {
      const data = await ListarIncidenciasPorPatioYFecha({
        yardId: sanitized.yardId,
        startDate: sanitized.startDate,
        endDate: sanitized.endDate,
        token,
      });
      // console.log(data);
      setRows(Array.isArray(data) ? data : []);
      setLastQuery({ ...sanitized });
    } catch (err) {
      setRows([]);
      if (toastOnError) {
        pushToast('danger', err?.message || 'No se pudieron cargar las incidencias');
      }
    } finally {
      if (showSpinner) {
        setSearching(false);
      }
      setHasSearched(true);
    }
  }, [pushToast, token]);

  const reload = useCallback(async () => {
    if (!lastQuery || searching) return;
    await runQuery(lastQuery, { showSpinner: false });
  }, [lastQuery, runQuery, searching]);

  const handleRefresh = useCallback(async () => {
    if (!lastQuery) { pushToast('warning', 'Realiza una búsqueda primero'); return; }
    setReloading(true);
    try { await reload(); }
    finally { setReloading(false); }
  }, [lastQuery, reload, pushToast]);


  const openEdit = useCallback((row) => {
    setEditingId(row.id);
    const guardName = row.userName + " " + row.userLastName;
    setEditMeta({ guardName });
    setForm({
      description: row.description || '',
      creationDate: formatDateTimeForInput(row.creationDate),
      typeIncident: row.typeIncident ? String(row.typeIncident) : '',
      levelIncident: row.levelIncident ? String(row.levelIncident) : '',
      state: row.state ? String(row.state) : '',
      isEmergency: Boolean(row.isEmergency),
      incidentResult: row.incidentResult || '',
    });
    setEvidenceFile(null);
    setShowForm(true);
  }, []);

  const openMap = useCallback((incident) => {
    console.log("INCDENCIA PAGE PRINCIPAL")
    console.log(incident)

    setIncidentMap(incident);
    setMapModalOpen(true);
  }, [pushToast]);

  const closeMap = useCallback(() => {
    setMapModalOpen(false);
    setIncidentMap({});
  }, []);


  const openEvidence = useCallback((incident) => {

    setSelectedIncident(incident);
    setShowEvidenceModal(true);
  }, []);

  const closeEvidence = useCallback(() => {
    setShowEvidenceModal(false);
  }, []);

  const removeRow = useCallback(async (id) => {
    try {
      await deleteIncidentWithEvidenceApi({ id, token });
      await reload();
      pushToast('success', 'Incidencia eliminada correctamente');
    } catch (err) {
      pushToast('danger', err?.message || 'No se pudo eliminar la incidencia');
    }
  }, [reload, pushToast, token]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();
    const { description, creationDate, typeIncident, levelIncident, state, isEmergency, incidentResult } = form;

    if (!typeIncident || !levelIncident || !state || (editingId == null && !creationDate)) {
      pushToast('warning', 'Completa los campos obligatorios');
      return;
    }

    let payload = {
      description,
      creationDate: localDateTimeToIso(creationDate),
      typeIncident: Number(typeIncident),
      levelIncident: Number(levelIncident),
      isEmergency: Boolean(isEmergency),
      state: Number(state),
    };

    if (!payload.creationDate && editingId == null) {
      pushToast('warning', 'La fecha seleccionada no es válida');
      return;
    }

    try {
      if (editingId == null) {
        const res = await createIncidentWithEvidenceApi({ incidentData: payload, token });
        const success = res?.isSuccess ?? res === true;
        if (success) {
          if (evidenceFile && res?.value) {
            await uploadIncidentEvidenceApi({ incidentId: res.value, evidenceFile, token });
          }
          await reload();
          pushToast('success', 'Incidencia creada correctamente');
        } else {
          throw new Error(res?.message || 'No se pudo registrar la incidencia');
        }
      } else {
        const updatePayload = {
          typeIncident: Number(typeIncident),
          levelIncident: Number(levelIncident),
          isEmergency: Boolean(isEmergency),
          state: Number(state),
          incidentResult: incidentResult ?? '',
        };
        const res = await updateIncidentWithEvidenceApi({ id: editingId, incidentData: updatePayload, token });
        const success = res?.isSuccess ?? res === true;
        if (!success) {
          throw new Error(res?.message || 'No se pudo actualizar la incidencia');
        }
        await reload();
        pushToast('success', 'Incidencia actualizada correctamente');
      }
      setShowForm(false);
      setEditingId(null);
      setEditMeta({ guardName: '' });
      setEvidenceFile(null);
    } catch (err) {
      pushToast('danger', err?.message || 'Ocurrio un error al guardar');
    }
  }, [editingId, evidenceFile, form, pushToast, reload, token]);

  const handleSearchSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!yardId) {
      pushToast('warning', 'Selecciona un patio');
      return;
    }
    if (!fechaInicio || !fechaFin) {
      pushToast('warning', 'Selecciona las fechas de inicio y fin');
      return;
    }
    if (new Date(fechaInicio) > new Date(fechaFin)) {
      pushToast('warning', 'La fecha de inicio no puede ser mayor que la fecha fin');
      return;
    }
    await runQuery({ yardId, startDate: fechaInicio, endDate: fechaFin });
  }, [fechaFin, fechaInicio, pushToast, runQuery, yardId]);

  return (
    <>
      <div className="container-fluid">
        <div className="mb-2">
          <h2 className="mb-2 text-center">Incidencias</h2>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <form onSubmit={handleSearchSubmit}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-lg-3">
                  <label className="form-label">Patio</label>
                  <select
                    className="form-select"
                    value={yardId}
                    onChange={(e) => setYardId(e.target.value)}
                    disabled={yardLoading}
                  >
                    <option value="">Selecciona un patio...</option>
                    {yards.map((yard) => (
                      <option key={yard.id} value={yard.id}>
                        {yard.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-lg-3">
                  <label className="form-label">Fecha de inicio</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <label className="form-label">Fecha de fin</label>
                  <input
                    type="date"
                    className="form-control"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>
                <div className="col-8 col-lg-2 d-grid order-2 order-lg-4">
                  <button type="submit" className="btn btn-primary" disabled={searching}>
                    {searching ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                <div className="col-4 col-lg-1 d-grid order-3 order-lg-5">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    title={lastQuery ? 'Actualizar' : 'Realiza una búsqueda primero'}
                    onClick={handleRefresh}
                    disabled={!lastQuery || reloading || searching}
                    aria-label="Actualizar"
                  >
                    {reloading
                      ? (<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>)
                      : (<i className="bi bi-arrow-clockwise" />)
                    }
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="gp-toast-container">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`toast align-items-center text-bg-${toast.type} show`}
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <div className="d-flex">
                <div className="toast-body">{toast.msg}</div>
                <button
                  type="button"
                  className="btn-close btn-close-white me-2 m-auto"
                  onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
                  aria-label="Close"
                />
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mb-3 d-flex gap-2 justify-content-end">
          <i className="bi bi-arrow-clockwise" />
        </div> */}

        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light text-center">
                <tr>
                  <th>#</th>
                  <th style={{ width: 120 }}>Acciones</th>
                  <th style={{ width: 200 }}>Descripción</th>
                  <th style={{ width: 200 }}>Detalle de solución</th>
                  <th>Vigilante</th>
                  <th>Tipo</th>
                  <th>Nivel</th>
                  <th>Emergencia</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody className='text-center'>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={15} className="text-center text-muted py-4">
                      {searching
                        ? <Cargando />
                        : hasSearched
                          ? 'Sin registros'
                          : 'Selecciona un patio y rango de fechas y presiona Buscar'}
                    </td>
                  </tr>
                )}
                {rows.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-outline-danger"
                          title="Eliminar"
                          onClick={() => setConfirmTarget({ id: row.id })}
                        >
                          <i className="bi bi-trash" />
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          title="Modificar"
                          onClick={() => openEdit(row)}
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          className="btn btn-outline-warning"
                          title="Evidencias"
                          onClick={() => openEvidence(row)}
                        >
                          <i className="bi bi-images" />
                        </button>
                        <button
                          className="btn btn-outline-success"
                          title="Mapa"
                          onClick={() => openMap(row)}
                        >
                          <i className="bi bi-geo-alt-fill" />
                        </button>
                      </div>
                    </td>
                    <td className='text-start'>{row.description}</td>
                    <td className='text-start'>{row.incidentResult === null || row.incidentResult === "" ? 'Sin detalle' : row.incidentResult}</td>

                    <td>{row?.userName} {row?.userLastName}</td>
                    <td>{tipoIncidencia(row.typeIncident)}</td>
                    <td>{nivelIncidencia(row.levelIncident)}</td>
                    <td>{row.isEmergency ? 'Si' : 'No'}</td>
                    <td>
                      <span>{peDate(row.creationDate)} -  {peTime(row.creationDate)}</span>
                    </td>
                    <td>{stateLabels.get(row.state) || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/*MODAL  EDITAR INCIDENCIA */}
      <div className={`modal fade ${showForm ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={showForm}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <form onSubmit={saveForm}>
              <div className="modal-header">
                <h5 className="modal-title">{editingId == null ? 'Registrar Incidencia' : 'Modificar Incidencia'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="row">
                  {editingId != null && (
                    <>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label">Vigilante</label>
                        <input type="text" className="form-control" value={editMeta.guardName} disabled />
                      </div>

                    </>
                  )}

                  <div className="col-12 col-lg-6 mb-3">
                    <label className="form-label">Fecha de incidencia</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={form.creationDate}
                      onChange={(event) => setForm((prev) => ({ ...prev, creationDate: event.target.value }))}
                      required
                      readOnly={editingId != null}
                      disabled={editingId != null}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-control"
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    rows={3}
                    readOnly={editingId != null}
                    disabled={editingId != null}
                  />
                </div>



                {editingId != null && (
                  <div className="mb-3">
                    <label className="form-label">Detalle de solución</label>
                    <textarea
                      className="form-control"
                      value={form.incidentResult}
                      onChange={(event) => setForm((prev) => ({ ...prev, incidentResult: event.target.value }))}
                      rows={3}
                      placeholder="Describe cómo se atendió y cerró la incidencia"
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Tipo</label>
                  <select
                    className="form-select"
                    value={form.typeIncident}
                    onChange={(event) => setForm((prev) => ({ ...prev, typeIncident: event.target.value }))}
                    required
                  >
                    <option value="">Seleccionar tipo</option>
                    {TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Nivel</label>
                  <select
                    className="form-select"
                    value={form.levelIncident}
                    onChange={(event) => setForm((prev) => ({ ...prev, levelIncident: event.target.value }))}
                    required
                  >
                    <option value="">Seleccionar nivel</option>
                    {LEVEL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    value={form.state}
                    onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))}
                    required
                  >
                    <option value="">Seleccionar estado</option>
                    {STATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-check mb-3">
                  <input
                    id="incident-emergency"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.isEmergency}
                    onChange={(event) => setForm((prev) => ({ ...prev, isEmergency: event.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="incident-emergency">
                    Es emergencia
                  </label>
                </div>

                {editingId == null && (
                  <div className="mb-3">
                    <label className="form-label">Evidencia (opcional)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      onChange={(event) => setEvidenceFile(event.target.files?.[0] || null)}
                    />
                    {evidenceFile && (
                      <div className="form-text">Archivo seleccionado: {evidenceFile.name}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showForm && <div className="modal-backdrop fade show" onClick={() => setShowForm(false)} />}

      <div className={`modal fade ${confirmTarget ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={Boolean(confirmTarget)}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirmación</h5>
              <button type="button" className="btn-close" onClick={() => setConfirmTarget(null)} aria-label="Close" />
            </div>
            <div className="modal-body">Seguro que desea eliminar la incidencia?</div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmTarget(null)}>
                No
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const id = confirmTarget?.id;
                  setConfirmTarget(null);
                  if (id) {
                    await removeRow(id);
                  }
                }}
              >
                Si, eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
      {confirmTarget && <div className="modal-backdrop fade show" onClick={() => setConfirmTarget(null)} />}

      {/*Modal Mapa*/}
      <div className={`modal fade ${mapModalOpen ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={Boolean(mapModalOpen)}>
        <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Ubicacion del patio</h5>
              <div className="d-flex flex-wrap align-items-center gap-3 mx-5">
                <h6 className="d-flex align-items-center gap-1 my-auto">
                  Nivel:
                </h6>
                <small className="d-flex align-items-center gap-1">
                  Bajo: <img src={iconBajoPng} alt="Bajo" width={30} height={30} />
                </small>
                <small className="d-flex align-items-center gap-1">
                  Medio: <img src={iconMedioPng} alt="Medio" width={30} height={30} />
                </small>
                <small className="d-flex align-items-center gap-1">
                  Alto: <img src={iconAltoPng} alt="Alto" width={30} height={30} />
                </small>
              </div>

              <button type="button" className="btn-close" onClick={closeMap} aria-label="Close" />
            </div>
            <div className="modal-body">
              <MapLeaflet incidentObject={IncidentMap} />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeMap}>Cerrar</button>
            </div>
          </div>
        </div>
      </div>
      {mapModalOpen && <div className="modal-backdrop fade show" onClick={closeMap} />}

      <EvidenciasPorIncidencias incidencia={selectedIncident} show={showEvidenceModal} onClose={closeEvidence} />
    </>
  );
}
